import type { QueryClient } from '@tanstack/react-query'

/** Prefix keys — invalidates all pages/filters for a workflow. */
export const workflowListQueryKeys = {
  todo: (workflowId: string) => ['workflow-todo', workflowId] as const,
  pending: (workflowId: string) => ['workflow-pending', workflowId] as const,
  done: (workflowId: string) => ['workflow-done', workflowId] as const,
}

export const workflowDetailQueryKeys = {
  purchaseOrderValidation: (workflowId: string, purchaseOrderId: string) =>
    ['purchase-order-validation', workflowId, purchaseOrderId] as const,
  inbox: (workflowId: string) => ['inbox', workflowId] as const,
  inboxEmail: (workflowId: string, emailId: string) =>
    ['inbox-email', workflowId, emailId] as const,
  orderConfirmationDetail: (workflowId: string, orderConfirmationId: string) =>
    ['order-confirmation-detail', workflowId, orderConfirmationId] as const,
}

/** Lists stay cached for instant navigation; refetch in background when stale. */
export const workflowListQueryOptions = {
  staleTime: 0,
} as const

/** Detail views: show cached data instantly, refetch in background on every visit. */
export const workflowDetailQueryOptions = {
  staleTime: 0,
} as const

export function invalidateWorkflowOverviewLists(
  queryClient: QueryClient,
  workflowId: string | undefined,
): void {
  if (!workflowId) return
  void queryClient.invalidateQueries({ queryKey: workflowListQueryKeys.todo(workflowId) })
  void queryClient.invalidateQueries({ queryKey: workflowListQueryKeys.pending(workflowId) })
  void queryClient.invalidateQueries({ queryKey: workflowListQueryKeys.done(workflowId) })
  void queryClient.invalidateQueries({ queryKey: ['workflow-summaries'] })
}

/**
 * Invalidates all OC workflow data after any mutation. Pass the optional ids
 * to also invalidate the specific detail views that are in-flight.
 */
export function invalidateOcWorkflowData(
  queryClient: QueryClient,
  workflowId: string | undefined,
  opts?: {
    purchaseOrderId?: string
    orderConfirmationId?: string
    emailId?: string
  },
): void {
  if (!workflowId) return
  invalidateWorkflowOverviewLists(queryClient, workflowId)
  void queryClient.invalidateQueries({ queryKey: workflowDetailQueryKeys.inbox(workflowId) })
  if (opts?.purchaseOrderId) {
    void queryClient.invalidateQueries({
      queryKey: workflowDetailQueryKeys.purchaseOrderValidation(workflowId, opts.purchaseOrderId),
    })
  }
  if (opts?.orderConfirmationId) {
    void queryClient.invalidateQueries({
      queryKey: workflowDetailQueryKeys.orderConfirmationDetail(workflowId, opts.orderConfirmationId),
    })
  }
  if (opts?.emailId) {
    void queryClient.invalidateQueries({
      queryKey: workflowDetailQueryKeys.inboxEmail(workflowId, opts.emailId),
    })
  }
}
